import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from "react";
import { uniqueId } from "lodash";
import PropTypes from "prop-types";

import { alignItems, strCode } from "../../../../../../consts";
import { log, notificationService, t } from "../../../../../../services";
import { arrayUtil } from "../../../../../../utils";
import { releaseNotesUtils } from "../../utils/releaseNotesUtils";

import {
  AddButton,
  FlexContainers,
  Paragraph2,
  TooltipIcon,
  WrapperContainer
} from "../../../../../../components";
import { FWUVersionSelector } from "../../components/FWUVersionSelector/FWUVersionSelector";
import { ReleaseNotePreview } from "../../components/ReleaseNotePreview/ReleaseNotePreview";
import { SectionTitle } from "../../styled";

export const FirmwareSection = forwardRef(
  (
    {
      firmwareSectionData,
      isEdit,
      releaseNotesLoading,
      versionOptions = [],
      onFetchReleaseNotes = () => {}
    },
    ref
  ) => {
    const initialVersion = useMemo(() => {
      return { id: uniqueId(), version: null };
    }, []);

    const [selectedVersions, setSelectedVersions] = useState([initialVersion]);

    const [isValid, setIsValid] = useState(true);

    const [showErrors, setShowErrors] = useState(false);
    const [errorIds, setErrorIds] = useState([]);

    const addFirmwareDisabled = useMemo(() => {
      return (
        !!selectedVersions.find((config) => !config.version) ||
        selectedVersions.length === versionOptions.length ||
        isEdit
      );
    }, [selectedVersions, isEdit]);

    const selectedVersionsList = useMemo(() => {
      return selectedVersions.map((config) => config.version?.id);
    }, [selectedVersions]);

    const releaseNotes = useMemo(() => {
      if (selectedVersionsList?.length && !releaseNotesLoading) {
        return releaseNotesUtils.getReleaseNotes(selectedVersionsList);
      }

      return null;
    }, [selectedVersionsList, releaseNotesLoading]);

    const getFirmwareSectionData = useCallback(() => {
      let newData = {
        selectedVersions
      };

      return newData;
    }, [selectedVersions]);

    useEffect(() => {
      prepopulateData();

      return () => {
        resetFormData();
      };
    }, [firmwareSectionData]);

    useEffect(() => {
      const emptyVersionIds = selectedVersions
        .filter((config) => !config.version)
        .map((config) => config.id);
      let valid = !emptyVersionIds.length;

      setErrorIds(emptyVersionIds);
      setIsValid(valid);
    }, [selectedVersions]);

    useEffect(() => {
      if (selectedVersionsList?.length) {
        const version = selectedVersionsList[selectedVersionsList.length - 1];

        if (version) {
          onFetchReleaseNotes(version);
        }
      }
    }, [selectedVersionsList]);

    const prepopulateData = useCallback(() => {
      if (firmwareSectionData) {
        const selectedVersionsList = firmwareSectionData?.selectedVersions.map(
          (v) => v.version?.id
        );
        const versionOptionsIds = versionOptions.map((v) => v.id);

        if (arrayUtil.includesAll(selectedVersionsList, versionOptionsIds)) {
          setSelectedVersions(firmwareSectionData?.selectedVersions);
        } else {
          resetFormData();
        }
      }
    }, [firmwareSectionData, versionOptions]);

    const resetFormData = () => {
      setSelectedVersions([initialVersion]);

      setIsValid(true);
      setShowErrors(false);
      setErrorIds([]);
    };

    useImperativeHandle(ref, () => ({
      saveFirmwareSectionData(validate = true) {
        if (!showErrors && validate) {
          setShowErrors(true);
        }

        if (!isValid && validate) {
          log.warn("FirmwareSection: Unable to save firmware section due to missing fields");

          notificationService.showError({
            title: t(strCode.MISSING_FIELDS),
            message: t(strCode.MISSING_FIELDS_MESSAGE)
          });
          return;
        }

        const data = getFirmwareSectionData();
        return data;
      }
    }));

    const handleSelectVersion = (id, version) => {
      const updatedVersionIdx = selectedVersions.findIndex((config) => config.id === id);
      const updatedVersion = { id, version };

      if (updatedVersionIdx !== -1) {
        const newSelectedVersions = [
          ...selectedVersions.slice(0, updatedVersionIdx),
          updatedVersion,
          ...selectedVersions.slice(updatedVersionIdx + 1)
        ];

        setSelectedVersions(newSelectedVersions);
      }
    };

    const handleDeleteVersion = (id) => {
      const newSelectedVersions = selectedVersions.filter((config) => config.id !== id);
      setSelectedVersions(newSelectedVersions);
    };

    const onAddFirmware = () => {
      const newSelectedVersions = [...selectedVersions, { id: uniqueId(), version: null }];
      setSelectedVersions(newSelectedVersions);
    };

    return (
      <WrapperContainer
        Container={FlexContainers.FlexColumnStart}
        alignItems={alignItems.start}
        gap={16}
        width={"100%"}>
        <WrapperContainer Container={FlexContainers.FlexRowStart}>
          <SectionTitle>{t(strCode.FIRMWARE_TITLE)}</SectionTitle>
          <WrapperContainer Container={FlexContainers.FlexColumnStart} pl={5}>
            <TooltipIcon tooltipWidth="290px">{t(strCode.FIRMWARE_HINT)}</TooltipIcon>
          </WrapperContainer>
        </WrapperContainer>
        <WrapperContainer
          Container={FlexContainers.FlexColumnStart}
          alignItems={alignItems.start}
          gap={24}
          width={"100%"}>
          <WrapperContainer
            Container={FlexContainers.FlexColumnStart}
            alignItems={alignItems.start}
            width={"100%"}>
            {selectedVersions.map((config, index) => {
              return (
                <FWUVersionSelector
                  key={config.id}
                  id={config.id}
                  version={config.version}
                  versionOptions={versionOptions}
                  prevVersion={selectedVersions[index - 1]?.version?.id}
                  nextVersion={selectedVersions[index + 1]?.version?.id}
                  handleSelectVersion={handleSelectVersion}
                  handleDeleteVersion={
                    selectedVersionsList?.length > 1 ? handleDeleteVersion : null
                  }
                  hasError={errorIds.includes(config.id)}
                  showErrors={showErrors}
                  disabled={isEdit || releaseNotesLoading}
                />
              );
            })}
          </WrapperContainer>

          <AddButton
            onClick={onAddFirmware}
            disabled={addFirmwareDisabled || releaseNotesLoading || true}>
            {t(strCode.ADD_FIRMWARE_BTN)}
          </AddButton>
        </WrapperContainer>

        {releaseNotesLoading && (
          <WrapperContainer
            Container={FlexContainers.FlexColumnCenter}
            width={"100%"}
            mt={48}
            textAlign={"center"}>
            <Paragraph2>{t(strCode.LOADING)}</Paragraph2>
          </WrapperContainer>
        )}

        {releaseNotes && (
          <ReleaseNotePreview
            version={!releaseNotes?.isCombined ? releaseNotes?.version : null}
            releaseNotesType={releaseNotes?.type}
            releaseNotes={releaseNotes?.data}
            mediaData={releaseNotes?.mediaData}
            thumbnailsData={releaseNotes?.thumbnailsData}
            onFetchReleaseNotes={onFetchReleaseNotes}
          />
        )}
      </WrapperContainer>
    );
  }
);

FirmwareSection.displayName = "FirmwareSection";

FirmwareSection.propTypes = {
  firmwareSectionData: PropTypes.shape({
    selectedVersions: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        version: PropTypes.shape({
          id: PropTypes.string.isRequired,
          value: PropTypes.string.isRequired
        })
      })
    ).isRequired
  }),
  versionOptions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired
    }).isRequired
  ).isRequired,
  isEdit: PropTypes.bool.isRequired,
  releaseNotesLoading: PropTypes.bool.isRequired,

  onFetchReleaseNotes: PropTypes.func.isRequired
};
